// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ChartData, ChartLineInfo } from "@stoneage/App/Services/JettingApp/JettingAppService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { xParserOptions } from "@stoneage/XDL/Utilities";
import { getSystemColor } from "@stoneage/XDL/Utilities";
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title as ChartTitle,
    Tooltip as ChartTooltip,
} from "chart.js";
import { _DeepPartialObject } from "chart.js/dist/types/utils";
import parse from "html-react-parser";
import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import { connect } from "react-redux";

interface OwnProps {
    label: string;
    chartData: ChartData[];
    tooltipText: string;
    extendedStyles?: JettingAppGraphStyles;
}

export interface JettingAppGraphStyles {
    wrapper?: InjectableCss;
    legendsWrapper?: InjectableCss;
    legendWrapper?: InjectableCss;
    legendLabel?: TypographyPresentationProps;
    legendIcon?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
    chartTitle?: TypographyPresentationProps;
    graphWrapper?: InjectableCss;
    canvasWrapper?: InjectableCss;
}

export const jettingAppGraphStyles: JettingAppGraphStyles = {};

const axisLabelFont = {
    size: 12,
    family: "'Roboto', sans-serif",
    weight: "normal" as "normal" | "bold" | "lighter" | "bolder",
    lineHeight: "18px",
};
const mapStateToProps = (state: CustomAppState) => ({
    colors: state.xJettingAppCalculators?.graphColors,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const JettingAppGraph = ({ extendedStyles, label, tooltipText, chartData, colors }: Props) => {
    const [styles] = useState(() => mergeToNew(jettingAppGraphStyles, extendedStyles));
    // Register required modules
    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        PointElement,
        LineElement,
        ChartTitle,
        ChartTooltip,
        Legend,
    );

    if (!chartData || !chartData.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.chartTitle}>
                {label}
                {tooltipText && (
                    <Tooltip {...styles.tooltip} passAsChildren={true}>
                        <Typography>{parse(tooltipText, xParserOptions)}</Typography>
                    </Tooltip>
                )}
            </Typography>
            {chartData.map((chart, index) => {
                const datasets = chart.seriesList.map((dataset: ChartLineInfo, index: number) => ({
                    label: dataset.label,
                    data: dataset.dataList,
                    backgroundColor: colors?.[index]?.name || getSystemColor("common.border"),
                    borderColor: colors?.[index]?.name || getSystemColor("common.border"),
                    yAxisID: dataset.axis,
                    tension: 0.4,
                }));
                const newData = { datasets };

                const scales = {
                    x: {
                        type: "linear" as const, // Ensure x-axis is numeric
                        title: {
                            display: true,
                            text: chart.xAxisLabel,
                            color: getSystemColor("text.main"),
                            font: axisLabelFont,
                        },
                        beginAtZero: true,
                        grid: {
                            drawOnChartArea: true,
                        },
                    },
                    ...chart.seriesList.reduce((acc: any, dataset: any, index: number) => {
                        acc[`${dataset.axis}`] = {
                            type: "linear" as const,
                            position:
                                index % 2 === 0
                                    ? "left"
                                    : ("right" as "bottom" | "left" | "right" | "top" | "center" | "chartArea"), // Alternate positions
                            title: {
                                display: !!dataset.axisLabel,
                                text: dataset.axisLabel,
                                color: getSystemColor("text.main"),
                                font: axisLabelFont,
                            },
                            grid: {
                                drawOnChartArea: index === chart.seriesList.length - 1, // Draw grid only for one axis
                            },
                            beginAtZero: true,
                        };
                        return acc;
                    }, {}),
                };

                const options = {
                    responsive: true,
                    plugins: {
                        legend: {
                            display: false,
                        },
                        title: {
                            display: !!chart.title,
                            text: chart.title,
                            color: getSystemColor("text.main"),
                            font: {
                                size: 15,
                                weight: "bold" as "normal" | "bold" | "lighter" | "bolder",
                                lineHeight: "26px",
                                family: "'Roboto', sans-serif",
                            },
                            align: "start" as "start" | "end" | "center",
                            padding: {
                                bottom: 8,
                            },
                        },
                    },
                    scales,
                };

                return (
                    <StyledWrapper {...styles.graphWrapper} key={index}>
                        <StyledWrapper {...styles.canvasWrapper}>
                            <Line data={newData} options={options} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.legendsWrapper}>
                            {/* Custom Vertical Legend */}
                            {newData.datasets.map((dataset, index: number) => (
                                <StyledWrapper {...styles.legendWrapper} key={index}>
                                    <Typography
                                        {...styles.legendIcon}
                                        {...{
                                            background: index < (colors?.length || -1) ? colors?.[index]?.name : "#CCC",
                                        }}
                                    ></Typography>
                                    <Typography {...styles.legendLabel} translate="no">
                                        {dataset.label}
                                    </Typography>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};
export default connect(mapStateToProps)(JettingAppGraph);
